import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building2,
  CalendarClock,
  ChevronDown,
  CreditCard,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  Puzzle,
  Settings,
  Users,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NovaBrand } from "@/components/common/NovaBrand";
import { ThemeToggle } from "@/components/common/ThemeToggle";
import { LastUpdated } from "@/components/common/LastUpdated";
import { PageMetaProvider, usePageMeta } from "@/context/PageMetaContext";
import { useNewItemBadges } from "@/hooks/useNewItemBadges";

const NAV_ITEMS = [
  { label: "Overview", path: "/admin/overview", icon: LayoutDashboard, subtitle: "Platform summary and key insights" },
  { label: "Demo Requests", path: "/admin/demo-requests", icon: CalendarClock, subtitle: "Manage and follow up with businesses requesting a Nova Opsis demo.", badgeKey: "demo_requests" },
  { label: "Quotes", path: "/admin/quotes", icon: FileText, subtitle: "Create, send and track quotes for prospective customers.", badgeKey: "quotes" },
  { label: "Organizations", path: "/admin/organizations", icon: Building2, subtitle: "Manage businesses using Nova Opsis.", badgeKey: "organizations" },
  { label: "Users", path: "/admin/users", icon: Users, subtitle: "Search and review users across organizations." },
  { label: "Solutions", path: "/admin/solutions", icon: Puzzle, subtitle: "Manage Nova Opsis capabilities available to organizations." },
  { label: "Billing", path: "/admin/billing", icon: CreditCard, subtitle: "Nova Opsis billing relationships with organizations." },
  { label: "Platform Settings", path: "/admin/settings", icon: Settings, subtitle: "Platform-wide defaults and safe operational controls." },
];

const SidebarContent = ({ onNavigate, badges = {} }) => (
  <div className="flex h-full flex-col bg-white text-slate-900 dark:bg-[#000103] dark:text-white">
    <div className="border-b border-slate-200 px-6 py-6 dark:border-white/10">
      <NovaBrand className="w-full" />
      <p className="mt-4 px-0.5 text-[10px] font-bold uppercase tracking-[0.22em] text-violet-600 dark:text-violet-300" data-testid="admin-portal-label">Platform Admin</p>
    </div>

    <div className="px-5 pt-6">
      <nav className="mt-0 space-y-1.5" data-testid="admin-sidebar-nav">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-semibold transition-colors duration-200 ${
                isActive
                  ? "bg-gradient-to-r from-[#0052FF] to-[#7B61FF] text-white shadow-[0_10px_30px_rgb(0,82,255,0.28)]"
                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white"
              }`
            }
          >
            <item.icon size={19} strokeWidth={1.8} />
            {item.label}
            {item.badgeKey && badges[item.badgeKey] > 0 && (
              <span
                data-testid={`admin-nav-badge-${item.badgeKey}`}
                className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white"
              >
                {badges[item.badgeKey] > 9 ? "9+" : badges[item.badgeKey]}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>

    <div className="mt-auto px-5 pb-6">
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
        <div className="flex items-center gap-3">
          <img src="/nova-logo-transparent.png" alt="" className="h-8 w-8 object-contain" />
          <div>
            <p className="text-sm font-bold text-slate-900 dark:text-white">Nova Opsis Platform</p>
            <p className="mt-1 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
              <span className="h-2 w-2 rounded-full bg-emerald-400" />
              Admin session active
            </p>
          </div>
        </div>
      </div>
      <p className="mt-5 text-center text-[10px] text-slate-400 dark:text-slate-600">© 2026 Nova Opsis. All rights reserved.</p>
    </div>
  </div>
);

export default function AdminLayout() {
  const { user, logout, API } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const badges = useNewItemBadges(API);
  const active = NAV_ITEMS.find((item) => location.pathname.startsWith(item.path)) || NAV_ITEMS[0];

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.title = `${active.label} — Nova Opsis Platform Admin`;
  }, [active.label]);

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  const initials = (user?.name || user?.email || "PA")
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <PageMetaProvider>
    <div className="min-h-screen overflow-x-hidden bg-[#F8FAFC] text-slate-950 dark:bg-[#0B1220] dark:text-white" data-testid="platform-admin-portal">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-72 lg:block">
        <SidebarContent badges={badges} />
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <motion.button
              type="button"
              aria-label="Close navigation"
              data-testid="admin-mobile-nav-backdrop"
              className="absolute inset-0 bg-slate-950/45"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="absolute inset-y-0 left-0 w-80 max-w-[86vw]"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            >
              <SidebarContent onNavigate={() => setMobileOpen(false)} badges={badges} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/92 backdrop-blur-xl dark:border-white/10 dark:bg-[#0B1220]/90">
          <div className="flex min-h-[76px] items-center justify-between gap-4 px-5 sm:px-7 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                data-testid="admin-mobile-menu-btn"
                className="rounded-xl border border-slate-200 p-2 text-slate-700 lg:hidden dark:border-white/15 dark:text-slate-200"
                aria-label="Open navigation"
              >
                <Menu size={20} />
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="truncate text-2xl font-extrabold tracking-tight text-slate-950 dark:text-white" data-testid="admin-page-title">
                    {active.label}
                  </h1>
                  <HeaderLastUpdated />
                </div>
                <p className="hidden truncate text-sm text-slate-500 sm:block dark:text-slate-400" data-testid="admin-page-subtitle">
                  {active.subtitle}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <div className="relative">
              <button
                type="button"
                onClick={() => setProfileOpen((value) => !value)}
                data-testid="admin-profile-menu-btn"
                className="flex items-center gap-3 rounded-xl px-2 py-1.5 transition-colors duration-200 hover:bg-slate-100 dark:hover:bg-white/10"
              >
                {user?.picture ? (
                  <img src={user.picture} alt="" className="h-9 w-9 rounded-full object-cover" />
                ) : (
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#0052FF] to-[#00D4FF] text-xs font-extrabold text-white">
                    {initials}
                  </span>
                )}
                <span className="hidden text-left sm:block">
                  <span className="block text-sm font-bold text-slate-950 dark:text-white" data-testid="admin-profile-name">{user?.name || user?.email}</span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">Platform Admin</span>
                </span>
                <ChevronDown size={16} className="text-slate-400 dark:text-slate-500" />
              </button>
              {profileOpen && (
                <div className="absolute right-0 mt-2 w-64 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg dark:border-white/10 dark:bg-[#111C33]" data-testid="admin-profile-menu">
                  <div className="border-b border-slate-100 px-3 py-3 dark:border-white/10">
                    <p className="text-sm font-bold text-slate-950 dark:text-white">{user?.name || "Platform Admin"}</p>
                    <p className="mt-0.5 break-all text-xs text-slate-500 dark:text-slate-400" data-testid="admin-profile-email">{user?.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleLogout}
                    data-testid="admin-sign-out-btn"
                    className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-red-600 transition-colors duration-200 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
                  >
                    <LogOut size={16} />
                    Sign out
                  </button>
                </div>
              )}
              </div>
            </div>
          </div>
        </header>

        <main className="p-5 sm:p-7 lg:p-8" data-testid="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
    </PageMetaProvider>
  );
}

const HeaderLastUpdated = () => {
  const meta = usePageMeta();
  return <LastUpdated timestamp={meta.timestamp} testId={meta.testId || "admin-page-last-updated"} />;
};
